import { ITransformItem, TransformType } from "@kuika/kuika-cl-model";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import "./transform-accordion.scss";
import TransformForm, { defaultsForType } from "./transform-form";
import TransformRow from "./transform-row";

export interface TransformAccordionProps {
  /** Current array of transform items. Always passed by the parent (controlled). */
  items: ITransformItem[];
  /** Called whenever the array changes (add / remove / item edit). */
  onChange: (next: ITransformItem[]) => void;
  /** Disable interaction (state machine transitioning, etc.). */
  disabled?: boolean;
  /**
   * Optional popover content rendered behind the ⚙ button in the header.
   * When provided, the gear button anchors a KuikaPopover whose content is
   * this node. The KuikaPopover lives inside the accordion (only the gear
   * button is the trigger) so row / ＋ / trash clicks don't bubble to a
   * parent popover wrapper.
   */
  settingsPopoverContent?: React.ReactNode;
  /** Optional popover title (rendered at the top of the settings popover). */
  settingsPopoverTitle?: string | React.ReactNode;
  /** Test id base. */
  dataTestId?: string;
}

const summarizeTransform = (item: ITransformItem): string => {
  const x: number = item.x ?? 0;
  const y: number = item.y ?? 0;
  const z: number = item.z ?? 0;
  switch (item.type) {
    case "move":
      return `Move: ${x}px, ${y}px, ${z}px`;
    case "scale":
      return `Scale: ${x}, ${y}, ${z}`;
    case "rotate":
      return `Rotate: ${x}deg, ${y}deg, ${z}deg`;
    case "skew":
      return `Skew: ${x}deg, ${y}deg`;
    default:
      return item.type;
  }
};

const createTransform = (type: TransformType = "move"): ITransformItem => ({
  id: uuidv4(),
  type,
  ...defaultsForType(type)
});

/**
 * TransformAccordion — Webflow-style multi-item editor for the
 * `transforms: ITransformItem[]` array. Renders the section header (with ⚙
 * and ＋ icons), the row list, and an inline accordion that mounts directly
 * under the selected row. State for "which row is open" is local to the
 * accordion; the array itself is controlled by the parent.
 *
 * Manual array management is intentional (not antd Form.List) so that the
 * inline-accordion DOM reposition and the dynamic per-type form do not fight
 * Form.List mount/unmount semantics.
 */
const TransformAccordion: React.FC<TransformAccordionProps> = ({
  items,
  onChange,
  disabled,
  settingsPopoverContent,
  settingsPopoverTitle,
  dataTestId
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);

  // Reconcile active row when the array changes (item removed externally).
  useEffect((): void => {
    if (activeId === null) return;
    const stillPresent: boolean = items.some((it: ITransformItem): boolean => it.id === activeId);
    if (!stillPresent) setActiveId(null);
  }, [items, activeId]);

  const activeIndex: number = activeId ? items.findIndex((it: ITransformItem): boolean => it.id === activeId) : -1;
  const activeItem: ITransformItem | undefined = activeIndex >= 0 ? items[activeIndex] : undefined;

  const handleAdd = (): void => {
    if (disabled) return;
    const next: ITransformItem = createTransform("move");
    onChange([...items, next]);
    setActiveId(next.id);
  };

  const handleRemove = (index: number): void => {
    if (disabled) return;
    const removed: ITransformItem | undefined = items[index];
    onChange(items.filter((_: ITransformItem, i: number): boolean => i !== index));
    if (removed && removed.id === activeId) setActiveId(null);
  };

  const handleSelect = (index: number): void => {
    const target: ITransformItem | undefined = items[index];
    if (!target) return;
    setActiveId((curr: string | null): string | null => (curr === target.id ? null : target.id));
  };

  const handleItemChange = (next: ITransformItem): void => {
    if (disabled) return;
    onChange(items.map((it: ITransformItem): ITransformItem => (it.id === next.id ? next : it)));
  };

  const settingsButton: React.ReactNode = (
    <button
      type="button"
      aria-label="Transform settings"
      aria-haspopup="dialog"
      aria-expanded={isSettingsOpen}
      className="transform-accordion__icon-button"
      onClick={(e: React.MouseEvent): void => {
        e.stopPropagation();
      }}
      disabled={disabled}
      data-testid={dataTestId ? `${dataTestId}-settings` : undefined}
    >
      <KuikaSvg name="settings" type="sidebar-icons" width={14} height={14} />
    </button>
  );

  return (
    <div className="transform-accordion" data-testid={dataTestId}>
      <div className="transform-accordion__header">
        <KuikaSmallText style={{ color: "var(--dark-7)", fontWeight: 500 }}>2D &amp; 3D Transforms</KuikaSmallText>
        <div className="transform-accordion__actions">
          {settingsPopoverContent ? (
            <KuikaPopover
              visible={isSettingsOpen}
              onVisibleChange={setIsSettingsOpen}
              title={settingsPopoverTitle}
              position="left"
              align="end"
              content={settingsPopoverContent}
            >
              {settingsButton}
            </KuikaPopover>
          ) : null}
          <button
            type="button"
            aria-label="Add transform"
            className="transform-accordion__icon-button"
            onClick={(e: React.MouseEvent): void => {
              e.stopPropagation();
              handleAdd();
            }}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-add` : undefined}
          >
            <KuikaSvg name="plus" width={14} height={14} />
          </button>
        </div>
      </div>

      {items.length === 0 ? null : (
        <div className="transform-accordion__rows">
          {items.map((item: ITransformItem, index: number) => (
            <React.Fragment key={item.id}>
              <TransformRow
                item={item}
                index={index}
                isActive={item.id === activeId}
                summary={summarizeTransform(item)}
                onSelect={handleSelect}
                onRemove={handleRemove}
                dataTestId={dataTestId ? `${dataTestId}-row-${index}` : undefined}
              />
              {item.id === activeId && activeItem && (
                <div className="transform-accordion__inline-form">
                  <TransformForm
                    item={activeItem}
                    onChange={handleItemChange}
                    disabled={disabled}
                    dataTestId={dataTestId ? `${dataTestId}-form` : undefined}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransformAccordion;
export { summarizeTransform };
